import { useEffect, useRef, useState } from "react";
import { Styled } from "./styled";
import { useSelector } from "react-redux";
import { getDisplaySize } from "../DisplaySizeSelector/selector";
import { BUTTON_HEIGHT, FONT_SIZE } from "@lhd/constants";
import useScrollSync from "../useScrollSync";

interface IProps {
  element: HTMLElement | null;
  /**
   * Content inside `element` whose width drives the scroll range. `element`
   * itself keeps its box when its content grows, so observing only that one
   * misses columns being added or removed; pass this to keep the bar in sync.
   */
  contentElement?: HTMLElement | null;
  widthOffset?: number;
  onGetHeightScrollVertical?: (height: number) => void;
  onScroll?: () => void;
  extendHeight?: number;
}
const DEFAULT_EXTENT_HEIGHT = 3;
const CustomHorizontalScrollbar = ({
  element,
  contentElement,
  widthOffset = 0,
  onGetHeightScrollVertical,
  onScroll,
  extendHeight = DEFAULT_EXTENT_HEIGHT,
}: IProps) => {
  const scrollbarRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [scrollWidth, setScrollWidth] = useState(0);
  const [scrollbarYWidth, setScrollbarYWidth] = useState(0);
  const handleScrollbarScroll = useScrollSync({
    axis: "horizontal",
    element,
    scrollbarRef,
  });

  /* =========================
     Custom → Element
  ========================= */
  const handleScroll = () => {
    if (handleScrollbarScroll()) onScroll?.();
  };

  const displaySize = useSelector(getDisplaySize) as
    | keyof typeof FONT_SIZE
    | keyof typeof BUTTON_HEIGHT;

  const isCustomHeaderScroll =
    /\/annual_salary_summaries\/\d{4}\/arrangements/.test(location.pathname) ||
    /\/annual_salary_summaries\/\d{4}\/deductions/.test(location.pathname);

  const onGetHeightScrollVerticalRef = useRef(onGetHeightScrollVertical);
  useEffect(() => {
    onGetHeightScrollVerticalRef.current = onGetHeightScrollVertical;
  }, [onGetHeightScrollVertical]);

  const updateSize = () => {
    if (!element) return;

    const yScrollbar = element.offsetWidth - element.clientWidth;
    setScrollbarYWidth(yScrollbar);
    setWidth(element.clientWidth + yScrollbar);
    setScrollWidth(element.scrollWidth);
    if (onGetHeightScrollVerticalRef.current && scrollbarRef.current)
      onGetHeightScrollVerticalRef.current(
        scrollbarRef.current.offsetHeight + extendHeight,
      );
  };

  /* =========================
     Observe size
  ========================= */
  useEffect(() => {
    if (!element) return;

    updateSize();

    const ro = new ResizeObserver(updateSize);
    ro.observe(element);
    if (contentElement && contentElement !== element)
      ro.observe(contentElement);

    return () => ro.disconnect();
  }, [element, contentElement, extendHeight]);

  /* =========================
     Get height
  ========================= */
  useEffect(() => {
    if (!onGetHeightScrollVerticalRef.current || !scrollbarRef.current) return;
    updateSize();
  }, [extendHeight, element?.scrollWidth]);

  const ROUNDING_TOLERANCE = 2;
  if (width >= scrollWidth - ROUNDING_TOLERANCE) {
    onGetHeightScrollVertical && onGetHeightScrollVertical(0);
    return null;
  }

  return (
    <Styled
      ref={scrollbarRef}
      onScroll={handleScroll}
      style={{
        width: width - widthOffset,
      }}
      isCustomHeaderScroll={isCustomHeaderScroll}
    >
      <div
        className="horizontal-scroll-bar-inside"
        style={{
          width: scrollWidth - widthOffset + scrollbarYWidth,
        }}
      />
    </Styled>
  );
};

export default CustomHorizontalScrollbar;
